/**
 * Microsoft Clarity loader (session replay + heatmaps).
 *
 * Injects the official Clarity tag exactly once, and only when the active
 * environment enables it, a project id is set, and we are NOT on localhost —
 * local dev sessions must never be shipped to Microsoft (same isolation the
 * MSW mock layer keeps in main.ts).
 *
 * Clarity records the live DOM, so on a fintech surface the masking guardrails
 * are not optional. They are enforced two ways, both OUTSIDE this file:
 *   - project masking mode set to strict / "Mask all" in the Clarity dashboard
 *     (do this before any real user is recorded), and
 *   - `data-clarity-mask="true"` on sensitive nodes (GST/PAN, account numbers,
 *     amounts) for belt-and-braces masking of specific elements.
 * The dashboard's Microsoft-Advertising "Data sharing" must also be turned off,
 * and — as a non-essential tracker under GDPR/DPDP — loading gated on consent.
 */
export interface ClarityConfig {
  /** When false, the Clarity tag is never injected in this environment. */
  readonly enabled: boolean;
  /** Clarity project id from the Clarity dashboard (e.g. 'y8uecii6nw'). */
  readonly projectId: string;
}

type ClarityQueue = ((...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
  interface Window {
    clarity?: ClarityQueue;
  }
}

export function loadClarity(config: ClarityConfig): void {
  if (!config?.enabled || !config.projectId) return;

  const host = location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host === '') return;

  // Idempotent: federated entrypoints can re-run; never inject the tag twice.
  if (window.clarity) return;

  const queue: ClarityQueue = function (...args: unknown[]) {
    (queue.q = queue.q || []).push(args);
  };
  window.clarity = queue;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${encodeURIComponent(config.projectId)}`;
  const first = document.getElementsByTagName('script')[0];
  first?.parentNode?.insertBefore(script, first);
}
